import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { pushTopLevelMenuItemClick } from '../../../../helpers/gtm';
import MegaMenuItemSecondLevel from "./MegeMenuItemSecondLevel";
import MegeMenuImageSection from './MegeMenuImageSection';

const getPageNameGA4 = () => window?.drupalSettings?.g4DataLayerCommonVariables?.pagename;

const MegaMenuItem = ({
  title,
  url,
  index,
  subtree = {},
  obfuscate,
  obfuscateDataUrl,
  gtmDataTrack,
  disableForLanguage,
  class: className,
  classStyle,
  attributes,
  imagesBlock,
  hideMenuItem
}) => {
  const [secondLevelIsOpen, setSecondLevelIsOpen] = useState(false);
  const [thirdLevelIsOpenStates, setThirdLevelIsOpenStates] = useState(null);
  const menuRef = useRef(null);

  const pushGTMEvent = useCallback(() => {
    if (gtmDataTrack) {
      pushTopLevelMenuItemClick({
        pagename: getPageNameGA4(),
        type: gtmDataTrack,
        index: index + 1
      });
    }
  }, [gtmDataTrack, index]);

  const setPreselectedMenu = useCallback(() => {
    const preselected = Object.keys(subtree).find((key) => subtree[key].preselected);
    setThirdLevelIsOpenStates(preselected || null);
  }, [subtree]);

  const closeMenuSecondLevel = useCallback(() => {
    setSecondLevelIsOpen(false);
    setThirdLevelIsOpenStates(null);
    document.body.classList.remove('megamenuopen');
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        event.target.closest('.megamenu__second-level') === null
      ) {
        closeMenuSecondLevel();
      }
    };

    if (secondLevelIsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.classList.add('megamenuopen');
    } else {
      document.body.classList.remove('megamenuopen');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('megamenuopen');
    };
  }, [secondLevelIsOpen, closeMenuSecondLevel]);

  useEffect(() => {
    if (secondLevelIsOpen) {
      setPreselectedMenu();
    }
  }, [secondLevelIsOpen, setPreselectedMenu]);

  const toggleMenuSecondLevel = (event) => {
    if (event && event.type === "keydown") {
      if (event.key !== "Enter" && event.key !== " ") return;
    }

    pushGTMEvent();
    setSecondLevelIsOpen((prev) => !prev);
  };

  const renderLink = () => {
    let classFromBO = attributes?.class || '';
    let targetFromBO = attributes?.target || '';
    const hasChildren = Object.keys(subtree).length > 0;

    if (obfuscate && !hasChildren) {
      return (
        <span
          aria-label={title}
          role="button"
          tabIndex="0"
          className={cn(classFromBO, 'obfuscate')}
          onClick={pushGTMEvent}
          data-link={obfuscateDataUrl}
          data-target={targetFromBO}
        >
          {title}
        </span>
      );
    }

    if (hasChildren) {
      return (
        <span
          aria-label={title}
          role="button"
          tabIndex="0"
          className={cn(classFromBO, { active: secondLevelIsOpen })}
          onClick={toggleMenuSecondLevel}
          onKeyDown={toggleMenuSecondLevel}
        >
          {title}
        </span>
      );
    }

    return (
      <a
        href={url}
        aria-label={title}
        tabIndex="0"
        className={cn(classFromBO)}
        onClick={pushGTMEvent}
        data-target={targetFromBO}
      >
        {title}
      </a>
    );
  };

  if (disableForLanguage || hideMenuItem) return null;

  const isFrLang = window?.drupalSettings?.path?.currentLanguage === 'fr';

  return (
    <div
      className={cn(
        'megamenu__item megamenu__item_first-level',
        { 'megamenu__item_first-level-fr': isFrLang },
        classStyle
      )}
      ref={menuRef}
    >
      {renderLink()}

      {secondLevelIsOpen && (
        <div className="megamenu__second-level">
          <span className="megamenu__second-level-close" onClick={closeMenuSecondLevel} />
          <div className={cn('megamenu__second-level-container')}>
            <div className="megamenu__second-level-items-list" />

            {Object.keys(subtree).map((key) => (
              <MegaMenuItemSecondLevel
                {...subtree[key]}
                key={key}
                id={key}
                thirdLevelIsOpen={thirdLevelIsOpenStates === key}
                updateChildState={setThirdLevelIsOpenStates}
              />
            ))}
            <MegeMenuImageSection
              active={thirdLevelIsOpenStates}
              defaultImages={imagesBlock}
              sections={subtree}
            />
          </div>
        </div>
      )}
    </div>
  );
};

MegaMenuItem.propTypes = {
  title: PropTypes.string,
  url: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  subtree: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  obfuscate: PropTypes.bool,
  obfuscateDataUrl: PropTypes.string,
  gtmDataTrack: PropTypes.string,
  disableForLanguage: PropTypes.bool,
  class: PropTypes.string,
  classStyle: PropTypes.string,
  attributes: PropTypes.object,
  imagesBlock: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  hideMenuItem: PropTypes.bool,
};

export default MegaMenuItem;
